import { useState, type ReactNode } from 'react';
import '../../assets/styles/FormField.css';

interface FormFieldProps {
  id:           string;
  label:        string;
  value:        string;
  onChange:     (value: string) => void;
  onBlur?:      () => void;
  error?:       string | null;
  type?:        string;
  placeholder?: string;
  required?:    boolean;
  disabled?:    boolean;
  maxLength?:   number;
  infoText?:    string;
  suffix?:      ReactNode;
}

export default function FormField({
  id, label, value, onChange, onBlur,
  error, type = 'text', placeholder,
  required, disabled, maxLength, infoText, suffix,
}: FormFieldProps) {
  const [tooltipVisible, setTooltipVisible] = useState(false);

  return (
    <div className="form-field">
      <label htmlFor={id} className="form-field__label">
        {label}
        {required && <span className="form-field__required" aria-hidden="true"> *</span>}
      </label>
      <div className="form-field__input-wrapper">
        <input
          id={id}
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          aria-invalid={!!error}
          aria-describedby={[error ? `${id}-error` : '', infoText ? `${id}-info` : ''].filter(Boolean).join(' ') || undefined}
          className={`form-field__input${error ? ' form-field__input--error' : ''}${infoText ? ' form-field__input--has-info' : ''}${suffix ? ' form-field__input--has-suffix' : ''}`}
        />
        {suffix && (
          <div className="form-field__suffix-wrap">
            {suffix}
          </div>
        )}
        {infoText && (
          <div className="form-field__info-wrap">
            <button
              type="button"
              className="form-field__info-btn"
              aria-label={`More information about ${label}`}
              aria-expanded={tooltipVisible}
              aria-controls={`${id}-info`}
              onMouseEnter={() => setTooltipVisible(true)}
              onMouseLeave={() => setTooltipVisible(false)}
              onFocus={() => setTooltipVisible(true)}
              onBlur={() => setTooltipVisible(false)}
              tabIndex={0}
            >
              &#9432;
            </button>
            <div
              id={`${id}-info`}
              role="tooltip"
              className={`form-field__tooltip${tooltipVisible ? ' form-field__tooltip--visible' : ''}`}
            >
              {infoText}
            </div>
          </div>
        )}
      </div>
      {error && (
        <span id={`${id}-error`} className="form-field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
